import { SpringValues, useSpring } from '@react-spring/web';
import React from 'react';
import { ReactElement, useState, VoidFunctionComponent } from 'react';
import { useSpringReset } from '../../hooks/useSpringReset';
import { prepareProps } from '../../utils/propUtils';

export enum Direction {
  LeftToRight = 'leftToRight',
  RightToLeft = 'rightToLeft'
}

export type SlideInWithBackdropProps = {
  show: boolean;
  children: (
    slideStyle: SpringValues<{ transform: string }>,
    backdropStyle: SpringValues<{ opacity: number }>
  ) => ReactElement;
  /**
   * default: LeftToRight
   */
  direction?: Direction;
};

export const SlideInWithBackdrop: VoidFunctionComponent<SlideInWithBackdropProps> =
  React.memo((props) => {
    const {
      show,
      children,
      direction = Direction.LeftToRight
    } = prepareProps({ props });
    const [renderChildren, setRenderChildren] = useState(show);

    const reset = useSpringReset();
    const config = { restVelocity: 0.01 };

    const slideStyle = useSpring({
      from: { transform: directionToFromTransform[direction] },
      to: { transform: 'translateX(0%)' },
      reset,
      reverse: !show,
      config,
      onStart: () => {
        if (show) {
          setRenderChildren(true);
        }
      },
      onRest: () => {
        if (!show) {
          setRenderChildren(false);
        }
      }
    });

    const backdropStyle = useSpring({
      from: { opacity: 0 },
      to: { opacity: 1 },
      reverse: !show,
      config
    });

    return renderChildren ? children(slideStyle, backdropStyle) : null;
  });

const directionToFromTransform: Record<Direction, string> = {
  leftToRight: 'translateX(-100%)',
  rightToLeft: 'translateX(100%)'
};
